import {useState, type FormEvent} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PorQue} from '@/components/ui/por-que'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {LockGlyph} from '@/components/ui/icons'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {UploadTrigger} from '@/components/upload-trigger'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {FichaDatos} from '@/components/solicitudes-ips/ficha-datos'
import {CorreosEnviados, DocumentosArt16, MatrizDelCargo} from '@/components/solicitudes-ips/solicitud-anexos'
import {FichaDatos as FichaDeLaCasa} from '@/components/ui/ficha-datos'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useEvidenceUploadFlow, useRegisterEvidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {subirEvidencia313} from '@/components/solicitudes-ips/subir-evidencia-313'
import {
  ESTADO_TONE,
  useCargarEnviada,
  useDocumentoSolicitud,
  useEnviarSolicitud,
  useIpsList,
  useRenovarEnlace,
  useSobreDeEnvio,
  type MedioDeEnvio,
  type SolicitudIps,
} from '@/hooks/salud/medicina_laboral/use-solicitudes-ips'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

// Sin `kairos`: dejaría declarar «salió de Kairos» sobre un envío que Kairos no hizo.
const MEDIOS_POR_FUERA: MedioDeEnvio[] = ['correo_externo', 'plataforma_ips', 'entrega_fisica']

type DatosPorFuera = {constanciaEvidenceId: string | null; enviadaEl: string}

function validatePorFuera(datos: DatosPorFuera, hoy: string): string | null {
  if (!datos.constanciaEvidenceId) return 'correoIps.errors.constanciaFaltante'
  if (!datos.enviadaEl || (hoy !== '' && datos.enviadaEl > hoy)) return 'correoIps.errors.fechaDeEnvioFutura'
  return null
}

/** Sin el `GET /envio` nadie firma: el sobre se ve antes de firmarlo. */
function SobreQueNoCargo({onReintentar, onClose}: {onReintentar: () => void; onClose: () => void}) {
  const {t} = useTranslation()
  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('correoIps.title')}</DialogTitle>
          <DialogDescription>{t('correoIps.intro')}</DialogDescription>
        </DialogHeader>
        <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="correoIps-sobre-error">
          {t('correoIps.errors.sobreNoCargo')}
        </p>
        <Button size="sm" variant="outline" onClick={onReintentar}>
          {t('solicitudIps.reintentar')}
        </Button>
      </DialogContent>
    </Dialog>
  )
}

function EnviarDialog({
  businessId,
  solicitud,
  onClose,
}: {
  businessId: string
  solicitud: SolicitudIps | null
  onClose: () => void
}) {
  const {t} = useTranslation()
  const sobre = useSobreDeEnvio(businessId, solicitud?.id ?? null)
  const enviar = useEnviarSolicitud(businessId)
  const [motivo, setMotivo] = useState('')
  // El 409 `YA_ENVIADA` lo contesta una persona: un reenvío vuelve a sacar datos de salud hacia un tercero.
  const [confirmaReenvio, setConfirmaReenvio] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!solicitud) return null
  if (sobre.isError) return <SobreQueNoCargo onReintentar={() => void sobre.refetch()} onClose={onClose} />
  const datos = sobre.data
  const cambio = datos?.cargo_cambio ?? false
  const yaEnviada = datos?.ya_enviada ?? false
  const bloqueadoPorMotivo = cambio && motivo.trim().length === 0
  // Sin sobre no hay firma, y sin IPS elegida el 409 ya está contestado antes de pedirlo.
  const sinSobre = datos === undefined || datos.para === null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    enviar.mutate(
      {
        solicitudId: solicitud!.id,
        reenvio: confirmaReenvio,
        motivoCargoCambiado: cambio ? motivo.trim() : null,
      },
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('correoIps.title')}</DialogTitle>
          <DialogDescription>{t('correoIps.intro')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {datos && (
            <FichaDeLaCasa
              testId="correoIps-sobre"
              bloques={[
                {
                  clave: 'correo',
                  titulo: t('correoIps.sobre'),
                  filas: [
                    {
                      clave: 'para',
                      etiqueta: t('correoIps.para'),
                      valor: (
                        <span className="font-mono text-xs" data-testid="correoIps-para">
                          {datos.para ?? '—'}
                        </span>
                      ),
                    },
                    {
                      clave: 'copia',
                      etiqueta: t('correoIps.copia'),
                      valor: (
                        <span className="font-mono text-xs" data-testid="correoIps-copia">
                          {datos.copia.length > 0 ? datos.copia.join(', ') : t('correoIps.sinCopia')}
                        </span>
                      ),
                    },
                    {
                      clave: 'respondeA',
                      etiqueta: t('correoIps.respondeA'),
                      valor: (
                        <span className="font-mono text-xs">{datos.responde_a ?? t('correoIps.sinCorreoEmpresa')}</span>
                      ),
                    },
                    {
                      clave: 'asunto',
                      etiqueta: t('correoIps.asunto'),
                      valor: (
                        <span className="font-medium" data-testid="correoIps-asunto">
                          {datos.asunto}
                        </span>
                      ),
                    },
                  ],
                  procedencia: {
                    label: t('correoIps.queLleva'),
                    testId: 'correoIps-que-lleva',
                    contenidoTestId: 'correoIps-que-lleva-detalle',
                    contenido: (
                      <ul className="space-y-0.5">
                        <li>{t('correoIps.sinNombreEnAsunto')}</li>
                        <li>{t('correoIps.adjunto')}</li>
                      </ul>
                    ),
                  },
                },
              ]}
            />
          )}

          {datos && datos.para === null && (
            <p className="notice-amber rounded-lg px-3 py-2 text-sm" data-testid="correoIps-sin-ips">
              {t('correoIps.errors.sinIpsElegida')}{' '}
              <Link className="underline" to={`/businesses/${businessId}/ips`}>
                {t('solicitudIps.aIps')}
              </Link>
            </p>
          )}

          {yaEnviada && (
            <div className="notice-amber space-y-2 rounded-lg px-3 py-2 text-sm" data-testid="correoIps-reenvio">
              {/* `enviada_el` viene de la lista (puede estar vieja) y `ya_enviada` del GET fresco:
                  la fecha puede faltar, y `formatDateOnly('')` lanza RangeError. */}
              <p>
                {solicitud.enviada_el
                  ? t('correoIps.reenvio.aviso', {fecha: formatDateOnly(solicitud.enviada_el)})
                  : t('correoIps.reenvio.avisoSinFecha')}
              </p>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="accent-primary"
                  data-testid="correoIps-reenvio-confirmar"
                  checked={confirmaReenvio}
                  onChange={(e) => setConfirmaReenvio(e.target.checked)}
                />
                {t('correoIps.reenvio.confirmar')}
              </label>
            </div>
          )}

          {cambio && (
            <div className="space-y-2" data-testid="correoIps-cargo-cambio">
              <p className="notice-amber rounded-lg px-3 py-2 text-sm">{t('correoIps.cargoCambio.aviso')}</p>
              {/* A la lista y no a `position_id`: ese id es del perfil congelado, el del cargo viejo;
                  cuál es el nuevo lo decide la persona. */}
              <Button asChild size="sm" variant="outline" data-testid="correoIps-al-perfil">
                <Link to={`/businesses/${businessId}/sgsst/perfiles-cargo`}>{t('correoIps.cargoCambio.alPerfil')}</Link>
              </Button>
              <label htmlFor="correoIps-motivo" className="block text-sm font-medium">
                {t('correoIps.cargoCambio.motivo')}
              </label>
              <Textarea
                maxLength={tope('NOTA')}
                id="correoIps-motivo"
                data-testid="correoIps-motivo"
                placeholder={t('correoIps.cargoCambio.motivoPlaceholder')}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
          )}

          <FormDialogFooter
            error={error}
            pending={enviar.isPending || sobre.isPending}
            submitDisabled={bloqueadoPorMotivo || sinSobre || (yaEnviada && !confirmaReenvio)}
            submitLabel={cambio ? t('correoIps.cargoCambio.enviarIgual') : t('correoIps.enviar')}
            onCancel={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Mismo peso probatorio que el envío por Kairos (§7); los plazos corren desde la fecha declarada. */
function PorFueraDialog({
  businessId,
  solicitud,
  onClose,
}: {
  businessId: string
  solicitud: SolicitudIps | null
  onClose: () => void
}) {
  const {t} = useTranslation()
  const registrar = useRegisterEvidence(businessId)
  const cargar = useCargarEnviada(businessId)
  const sobre = useSobreDeEnvio(businessId, solicitud?.id ?? null)
  const [busquedaIps, setBusquedaIps] = useState('')
  const ips = useIpsList(businessId, {page: 1, search: busquedaIps})
  // El hoy lo da el backend (Bogotá): un navegador en otro huso pondría un tope que la API rechaza.
  const hoy = sobre.data?.hoy ?? ''
  const [constanciaEvidenceId, setConstanciaEvidenceId] = useState<string | null>(null)
  const [constanciaNombre, setConstanciaNombre] = useState('')
  const [enviadaEl, setEnviadaEl] = useState('')
  const [medio, setMedio] = useState<MedioDeEnvio>('correo_externo')
  const [ipsId, setIpsId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const upload = useEvidenceUploadFlow(async (file, onProgress) => {
    const nueva = await subirEvidencia313(businessId, registrar, file, onProgress)
    setConstanciaEvidenceId(nueva?.id ?? null)
    setConstanciaNombre(file.name)
  })

  if (!solicitud) return null
  // Sin el sobre no hay `hoy`, y sin él desaparece el tope de la fecha de envío.
  if (sobre.isError) return <SobreQueNoCargo onReintentar={() => void sobre.refetch()} onClose={onClose} />
  const sinIpsCongelada = solicitud.ips_id === null
  const opciones = ips.data?.items ?? []
  const elegida = opciones.find((fila) => fila.id === ipsId) ?? null

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const hallazgo = validatePorFuera({constanciaEvidenceId, enviadaEl}, hoy)
    setError(hallazgo ? t(hallazgo) : null)
    if (hallazgo) return
    cargar.mutate(
      {
        solicitudId: solicitud!.id,
        constanciaEvidenceId: constanciaEvidenceId!,
        enviadaEl,
        medio,
        // Solo sin IPS congelada (§9 pide archivo + fecha + medio + IPS): cambiar una congelada
        // reescribiría a quién se le pidió el examen.
        ipsId: sinIpsCongelada ? ipsId : null,
      },
      {onSuccess: onClose, onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('correoIps.porFuera.title')}</DialogTitle>
          <DialogDescription>{t('correoIps.porFuera.intro')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('correoIps.porFuera.constancia')}</p>
            <UploadTrigger
              label={t('correoIps.porFuera.subir')}
              progress={upload.progress}
              testId="correoIps-constancia"
              onPick={upload.start}
            />
            {constanciaNombre && (
              <p className="text-xs text-muted-foreground" data-testid="correoIps-constancia-nombre">
                {constanciaNombre}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t('correoIps.porFuera.quedaArchivada')}</p>
            <FieldError error={upload.error ?? undefined} />
          </div>

          <DateField
            id="correoIps-fecha"
            label={t('correoIps.porFuera.fecha')}
            value={enviadaEl}
            max={hoy}
            onChange={setEnviadaEl}
          />

          <div className="space-y-1">
            <span className="text-sm font-medium">{t('correoIps.porFuera.ips')}</span>
            {sinIpsCongelada ? (
              <EntitySearchSelect
                items={opciones}
                value={elegida}
                getKey={(fila) => fila.id}
                filterText={(fila) => `${fila.name} ${fila.correo_institucional}`}
                renderOption={(fila) => (
                  <span>
                    {fila.name}{' '}
                    <span className="font-mono text-xs text-muted-foreground">{fila.correo_institucional}</span>
                  </span>
                )}
                renderTriggerValue={(fila) => `${fila.name} · ${fila.correo_institucional}`}
                placeholder={t('solicitudIps.preparar.elegirIpsPlaceholder')}
                searchPlaceholder={t('ips.buscar')}
                noResultsLabel={t('solicitudIps.preparar.ipsSinResultados')}
                testId="correoIps-porFuera-ips"
                onQueryChange={setBusquedaIps}
                onPick={(fila) => setIpsId(fila?.id ?? null)}
              />
            ) : (
              <p className="text-sm" data-testid="correoIps-porFuera-ips-fija">
                {solicitud.ips_nombre}
                <span className="block text-xs text-muted-foreground">{t('correoIps.porFuera.ipsCongelada')}</span>
              </p>
            )}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">{t('correoIps.porFuera.medio')}</legend>
            {MEDIOS_POR_FUERA.map((opcion) => (
              <label key={opcion} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="correoIps-medio"
                  value={opcion}
                  data-testid={`correoIps-medio-${opcion}`}
                  checked={medio === opcion}
                  onChange={() => setMedio(opcion)}
                  className="mt-0.5 accent-primary"
                />
                <span>{t(`correoIps.porFuera.medios.${opcion}`)}</span>
              </label>
            ))}
          </fieldset>

          {/* Sin `progress`, «Cargar» durante la subida diría que falta la constancia. */}
          <FormDialogFooter
            error={error}
            pending={cargar.isPending || upload.progress !== null}
            submitLabel={t('correoIps.porFuera.guardar')}
            onCancel={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** El enlace renovado se muestra una vez y no se cachea: es un secreto de un solo uso. */
function EnlaceDeCarga({
  solicitud,
  businessId,
  puedeFirmar,
}: {
  solicitud: SolicitudIps
  businessId: string
  puedeFirmar: boolean
}) {
  const {t} = useTranslation()
  const renovar = useRenovarEnlace(businessId)
  const [enlace, setEnlace] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-1" data-testid={`cargaIps-enlace-${solicitud.id}`}>
      <p className="text-xs text-muted-foreground">
        {t(
          `cargaIps.estado.${
            solicitud.enlace_carga_estado === 'sin_enlace' && solicitud.origen === 'por_fuera'
              ? 'sin_enlace_por_fuera'
              : solicitud.enlace_carga_estado
          }`
        )}
        {/* El último día en que sirve, no el instante de vencimiento (que cae el día siguiente). */}
        {solicitud.enlace_carga_vence_dia && solicitud.enlace_carga_estado === 'vigente'
          ? ` ${t('cargaIps.venceEl', {fecha: formatDateOnly(solicitud.enlace_carga_vence_dia)})}`
          : ''}
      </p>
      {solicitud.carga_recibida_el && (
        <p className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`cargaIps-recibida-${solicitud.id}`}>
          {t('cargaIps.recibidaEl', {fecha: formatDateOnly(solicitud.carga_recibida_el)})}
          <PorQue label={t('common.porQue')} testId={`cargaIps-recibida-porque-${solicitud.id}`}>
            <p>{t('cargaIps.recibidaElPorque')}</p>
            {/* §7 obliga a guardar y mostrar la procedencia: la superficie de carga no tiene JWT. */}
            {solicitud.carga_recibida_ip && <p>{t('cargaIps.recibidaDesde', {ip: solicitud.carga_recibida_ip})}</p>}
          </PorQue>
        </p>
      )}
      {solicitud.carga_retenida_el && !solicitud.carga_recibida_el && (
        <div
          className="notice-amber space-y-1 rounded-lg px-3 py-2 text-xs"
          data-testid={`cargaIps-retenida-${solicitud.id}`}>
          <p>{t('cargaIps.retenidaEl', {fecha: formatDateOnly(solicitud.carga_retenida_el)})}</p>
          <PorQue label={t('common.porQue')} testId={`cargaIps-retenida-porque-${solicitud.id}`}>
            <p>{t('cargaIps.retenidaElPorque')}</p>
          </PorQue>
        </div>
      )}
      {solicitud.enlace_renovado_el && (
        <p className="text-xs text-muted-foreground" data-testid={`cargaIps-renovado-${solicitud.id}`}>
          {t('cargaIps.renovadoPor', {
            nombre: solicitud.enlace_renovado_por ?? t('cargaIps.renovadoPorLaEmpresa'),
            fecha: formatDateOnly(solicitud.enlace_renovado_el),
          })}
        </p>
      )}
      {/* Renovar uno `vigente` mataría el que la IPS ya tiene; `enlace_carga_renovable` evita el 409. */}
      {solicitud.enlace_carga_estado !== 'vigente' && solicitud.enlace_carga_renovable && (
        <Button
          size="sm"
          variant="outline"
          data-testid={`cargaIps-renovar-${solicitud.id}`}
          disabled={!puedeFirmar || renovar.isPending}
          onClick={() => {
            setError(null)
            renovar.mutate(solicitud.id, {
              onSuccess: (data) => setEnlace(data.enlace),
              onError: (e) => setError(translateApiError(e)),
            })
          }}>
          {t('cargaIps.renovar')}
        </Button>
      )}
      {enlace && (
        <div className="space-y-1">
          <p className="break-all font-mono text-xs" data-testid={`cargaIps-enlace-nuevo-${solicitud.id}`}>
            {enlace}
          </p>
          <Button
            size="sm"
            variant="outline"
            data-testid={`cargaIps-copiar-${solicitud.id}`}
            onClick={() => void navigator.clipboard.writeText(enlace)}>
            {t('cargaIps.copiar')}
          </Button>
        </div>
      )}
      {error && (
        <p className="notice-red rounded-lg px-3 py-2 text-xs" data-testid={`cargaIps-renovar-error-${solicitud.id}`}>
          {error}
        </p>
      )}
    </div>
  )
}

/** Copia congelada de lo que viajó: no se recalcula al abrirla. */
export function SolicitudDetalle({
  solicitud,
  businessId,
  puedeFirmar,
}: {
  solicitud: SolicitudIps
  businessId: string
  puedeFirmar: boolean
}) {
  const {t} = useTranslation()
  const custodiaSalud = useCapabilities(businessId).sgsst_custodiar_salud
  const documento = useDocumentoSolicitud(businessId)
  const [enviando, setEnviando] = useState(false)
  const [porFuera, setPorFuera] = useState(false)
  const sinAcuse = solicitud.estado === 'enviada_sin_acuse'

  return (
    <div className="space-y-3 px-4 py-3 text-sm" data-testid={`solicitudIps-detalle-${solicitud.id}`}>
      <FichaDatos
        datos={Object.entries(solicitud.datos as Record<string, string | null>).map(([clave, valor]) => ({
          clave,
          valor,
          origen: (solicitud.datos_origen as Record<string, string>)[clave],
        }))}
        testId={`solicitudIps-ficha-${solicitud.id}`}
      />

      {solicitud.enfasis.length > 0 && (
        <ul className="space-y-1">
          {solicitud.enfasis.map((item) => (
            <li key={`${item.texto}-${item.numero_fila ?? ''}`}>
              {item.texto}
              <span className="text-xs text-muted-foreground">
                {' — '}
                {t('solicitudIps.preparar.detalleEnfasis', {fila: item.numero_fila ?? ''})}
              </span>
              {item.quitado_motivo && (
                <span className="text-xs text-muted-foreground">
                  {' '}
                  {t('solicitudIps.enfasisQuitado', {motivo: item.quitado_motivo})}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {solicitud.sin_bateria && (
        <p className="notice-amber rounded-lg px-3 py-2" data-testid={`solicitudIps-sin-bateria-${solicitud.id}`}>
          {t('solicitudIps.sinBateria')}
        </p>
      )}

      {solicitud.enviada_el && (
        <div className="space-y-1" data-testid={`correoIps-envio-${solicitud.id}`}>
          <Tag tone={ESTADO_TONE[solicitud.estado]}>{t(`solicitudIps.estado.${solicitud.estado}`)}</Tag>
          {solicitud.enviada_a_correo && (
            <p className="text-xs text-muted-foreground">
              {t('correoIps.enviadaEl', {
                fecha: formatDateOnly(solicitud.enviada_el),
                correo: solicitud.enviada_a_correo,
              })}
            </p>
          )}
          {solicitud.mensaje_id && (
            <p className="font-mono text-xs text-muted-foreground" data-testid={`correoIps-mensaje-${solicitud.id}`}>
              {t('correoIps.mensajeId', {id: solicitud.mensaje_id})}
            </p>
          )}
          {solicitud.enviada_por && (
            <p className="text-xs text-muted-foreground" data-testid={`correoIps-firmante-${solicitud.id}`}>
              {t('correoIps.firmadaPor', {nombre: solicitud.enviada_por})}
            </p>
          )}
          {solicitud.enviada_motivo_cargo_cambiado && (
            <p className="text-xs text-muted-foreground" data-testid={`correoIps-motivo-${solicitud.id}`}>
              {t('correoIps.cargoCambio.motivoEscrito', {motivo: solicitud.enviada_motivo_cargo_cambiado})}
            </p>
          )}
          {solicitud.origen === 'por_fuera' && (
            <p className="text-xs text-muted-foreground" data-testid={`correoIps-declarada-${solicitud.id}`}>
              {t('correoIps.porFuera.enviadaEl', {fecha: formatDateOnly(solicitud.enviada_el)})}
            </p>
          )}
          {solicitud.origen === 'por_fuera' && (
            <p className="text-xs text-muted-foreground" data-testid={`correoIps-por-fuera-${solicitud.id}`}>
              {t('correoIps.porFuera.cargada', {
                nombre: solicitud.cargada_por ?? t('correoIps.porFuera.cargadaPorLaEmpresa'),
                // La del cargue: los plazos corren desde la declarada, no desde esta.
                fecha: solicitud.cargada_el ? formatDateOnly(solicitud.cargada_el) : '—',
              })}
            </p>
          )}
          {sinAcuse && (
            <PorQue label={t('common.porQue')} testId={`correoIps-sin-acuse-${solicitud.id}`}>
              <p>{t('correoIps.sinAcuse')}</p>
            </PorQue>
          )}
          <EnlaceDeCarga solicitud={solicitud} businessId={businessId} puedeFirmar={puedeFirmar} />
        </div>
      )}

      <MatrizDelCargo solicitud={solicitud} />
      <DocumentosArt16 solicitud={solicitud} />
      <CorreosEnviados solicitud={solicitud} />

      <div className="flex flex-wrap items-center gap-2">
        {custodiaSalud ? (
          <Button
            size="sm"
            variant="outline"
            data-testid={`solicitudIps-descargar-${solicitud.id}`}
            disabled={documento.isPending}
            onClick={() =>
              documento.mutate(solicitud.id, {onSuccess: (data) => window.open(data.download_url, '_blank')})
            }>
            {t('solicitudIps.descargar')}
          </Button>
        ) : (
          <span
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            data-testid={`solicitudIps-documento-reservado-${solicitud.id}`}>
            <LockGlyph />
            {t('evidence.soloCustodia')}
          </span>
        )}
        <Button
          size="sm"
          data-testid={`correoIps-abrir-${solicitud.id}`}
          disabled={!puedeFirmar}
          onClick={() => setEnviando(true)}>
          {t('correoIps.abrir')}
        </Button>
        {/* Sobre un envío de Kairos es 409. Mismo cinturón que enviar: no en modo dios solo-lectura. */}
        {!solicitud.enviada_el && (
          <Button
            size="sm"
            variant="outline"
            data-testid={`correoIps-por-fuera-abrir-${solicitud.id}`}
            disabled={!puedeFirmar}
            onClick={() => setPorFuera(true)}>
            {t('correoIps.porFuera.abrir')}
          </Button>
        )}
        <span className="text-xs text-muted-foreground">{t('solicitudIps.descargarNoEsEntregar')}</span>
      </div>

      {/* §9 pide el chequeo también antes de imprimir; avisa sin frenar: el papel ya se bajó. */}
      {documento.data?.cargo_cambio && (
        // Flex: `PorQue` sin `enFlujo` es un `<button>` en línea y se pegaba al texto sin espacio.
        <p
          className="notice-amber flex flex-wrap items-center gap-1.5 rounded-lg px-3 py-2 text-xs"
          data-testid={`solicitudIps-descarga-cargo-cambio-${solicitud.id}`}>
          <span>{t('correoIps.cargoCambio.alDescargar')}</span>
          <PorQue label={t('common.porQue')} testId={`solicitudIps-descarga-cargo-cambio-porque-${solicitud.id}`}>
            <span>{t('correoIps.cargoCambio.alDescargarPorque')}</span>
          </PorQue>
        </p>
      )}

      {enviando && <EnviarDialog businessId={businessId} solicitud={solicitud} onClose={() => setEnviando(false)} />}
      {porFuera && <PorFueraDialog businessId={businessId} solicitud={solicitud} onClose={() => setPorFuera(false)} />}
    </div>
  )
}
